import { DownloadOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { App, Button, Input, Popconfirm, Progress, Select, Space, Tag } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  deleteTicket,
  exportTickets,
  getRenderProgress,
  listTemplates,
  listTickets,
  renderAll,
  rerenderTicket,
  restoreTicket,
} from '../api';
import AddGuestsModal from '../components/add-guests-modal';
import TemplateEditor from '../components/template-editor';
import TemplatesSection from '../components/templates-section';
import { EditTicketModal, LinkTicketModal, RevokeModal, ViewTicketModal } from '../components/ticket-modals';
import TicketsTable from '../components/tickets-table';
import {
  GalaTemplate,
  GalaTicketRow,
  GalaTicketStatus,
  RenderProgress,
  TICKET_STATUS_META,
  TicketCounts,
  errorText,
  guestGroupOptions,
} from '../contract';
import { saveBlob } from '../lib/save-blob';
import styles from '../styles.module.scss';

interface Props {
  eventId: number;
}

const POLL_MS = 3000;
const EMPTY_COUNTS: TicketCounts = { issued: 0, pending: 0, revoked: 0, checkedIn: 0 };
/** Mục lọc thêm ở ô Trạng thái: vé chưa có ghế (gửi seated=0, không gửi status). */
const UNSEATED = 'UNSEATED';
type StatusFilter = GalaTicketStatus | typeof UNSEATED;

/** Tab Vé mời: mẫu vé, danh sách khách, thêm khách 4 cách, tiến độ vẽ (SPEC mục 8). */
const TicketsTab = ({ eventId }: Props) => {
  const { message } = App.useApp();
  const [templates, setTemplates] = useState<GalaTemplate[]>([]);
  const [rows, setRows] = useState<GalaTicketRow[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<TicketCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [status, setStatus] = useState<StatusFilter | undefined>();
  const [group, setGroup] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [progress, setProgress] = useState<RenderProgress | null>(null);
  const [busy, setBusy] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTpl, setEditingTpl] = useState<GalaTemplate | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editTicket, setEditTicket] = useState<GalaTicketRow | null>(null);
  const [linkTarget, setLinkTarget] = useState<GalaTicketRow | null>(null);
  const [viewTicket, setViewTicket] = useState<GalaTicketRow | null>(null);
  const [revokeList, setRevokeList] = useState<GalaTicketRow[]>([]);

  const requestRef = useRef(0);
  const known = useRef(new Map<number, GalaTicketRow>());
  const wasRendering = useRef(false);

  const reloadTemplates = useCallback(async () => {
    try {
      setTemplates(await listTemplates(eventId));
    } catch (e) {
      message.error(errorText(e, 'Không tải được danh sách mẫu vé.'));
    }
  }, [eventId, message]);

  const reloadTickets = useCallback(
    async (showSpinner = true) => {
      const ticket = ++requestRef.current;
      if (showSpinner) setLoading(true);
      try {
        const res = await listTickets(eventId, {
          ...(status === UNSEATED ? { seated: 0 as const } : { status }),
          group,
          q: search.trim() || undefined,
          page,
          limit,
        });
        if (ticket !== requestRef.current) return;
        res.data.forEach((r) => known.current.set(r.id, r));
        setRows(res.data);
        setTotal(res.total);
        setCounts(res.counts ?? EMPTY_COUNTS);
      } catch (e) {
        if (ticket === requestRef.current) message.error(errorText(e, 'Không tải được danh sách vé.'));
      } finally {
        if (ticket === requestRef.current) setLoading(false);
      }
    },
    [eventId, status, group, search, page, limit, message]
  );

  const reloadProgress = useCallback(async () => {
    try {
      const p = await getRenderProgress(eventId);
      setProgress(p);
      return p;
    } catch {
      return null; // lỗi tạm thời: thanh tiến độ giữ nguyên, lần poll sau thử lại
    }
  }, [eventId]);

  useEffect(() => {
    reloadTemplates();
    reloadProgress();
  }, [reloadTemplates, reloadProgress]);

  useEffect(() => {
    reloadTickets();
    return () => {
      requestRef.current += 1; // bỏ kết quả đang bay khi đổi bộ lọc / rời tab
    };
  }, [reloadTickets]);

  // Đang vẽ (queued > 0): hỏi tiến độ mỗi 3 giây; vẽ xong thì tải lại bảng để hiện ảnh vé.
  const rendering = (progress?.queued ?? 0) > 0;
  useEffect(() => {
    if (wasRendering.current && !rendering) reloadTickets(false);
    wasRendering.current = rendering;
    if (!rendering) return undefined;
    const timer = window.setInterval(async () => {
      await reloadProgress();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [rendering, reloadProgress, reloadTickets]);

  const afterChange = useCallback(async () => {
    await Promise.all([reloadTickets(false), reloadProgress()]);
  }, [reloadTickets, reloadProgress]);

  // Nhóm có sẵn + nhóm đang dùng ở mẫu vé / vé đang hiện / ô lọc.
  const groups = useMemo(
    () =>
      guestGroupOptions(
        templates.map((t) => t.guestGroup),
        rows.map((r) => r.guestGroup),
        [group]
      ),
    [templates, rows, group]
  );

  const selectedRows = useMemo(
    () => selectedIds.map((id) => known.current.get(id)).filter((r): r is GalaTicketRow => !!r),
    [selectedIds]
  );

  const guard = async (task: () => Promise<unknown>, ok: string, fail: string) => {
    setBusy(true);
    try {
      await task();
      if (ok) message.success(ok);
      await afterChange();
    } catch (e) {
      message.error(errorText(e, fail));
    } finally {
      setBusy(false);
    }
  };

  const rerenderSelected = async () => {
    setBusy(true);
    let failed = 0;
    for (const t of selectedRows) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await rerenderTicket(eventId, t.id);
      } catch {
        failed += 1;
      }
    }
    setBusy(false);
    if (failed) message.warning(`Xếp vẽ lại được ${selectedRows.length - failed}/${selectedRows.length} vé.`);
    else message.success(`Đã xếp ${selectedRows.length} vé vào hàng đợi vẽ lại.`);
    await afterChange();
  };

  const onExport = async () => {
    setBusy(true);
    try {
      const { blob, filename } = await exportTickets(eventId);
      saveBlob(blob, filename);
    } catch (e) {
      message.error(errorText(e, 'Không xuất được file Excel.'));
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = (s: GalaTicketStatus) => {
    setPage(1);
    setStatus((cur) => (cur === s ? undefined : s));
  };

  const countTag = (s: GalaTicketStatus, n: number) => (
    <Tag.CheckableTag key={s} checked={status === s} onChange={() => toggleStatus(s)}>
      {TICKET_STATUS_META[s].text}: <strong>{n}</strong>
    </Tag.CheckableTag>
  );

  const percent = progress && progress.total > 0 ? Math.round(((progress.rendered + progress.failed) / progress.total) * 100) : 0;
  const revokable = selectedRows.filter((t) => t.status !== 'REVOKED');

  return (
    <div>
      <TemplatesSection
        eventId={eventId}
        templates={templates}
        onAdd={() => {
          setEditingTpl(null);
          setEditorOpen(true);
        }}
        onEdit={(t) => {
          setEditingTpl(t);
          setEditorOpen(true);
        }}
        onChanged={reloadTemplates}
      />

      <section className={styles.card} aria-label="Danh sách khách mời">
        <h3 className={styles.cardTitle}>Danh sách khách mời</h3>

        <div className={styles.statusBar}>
          {countTag('ISSUED', counts.issued)}
          {countTag('PENDING_MATCH', counts.pending)}
          {countTag('REVOKED', counts.revoked)}
          <Tag color="processing">Đã check-in: {counts.checkedIn}</Tag>
        </div>

        {progress && progress.total > 0 && (rendering || progress.failed > 0) && (
          <div style={{ marginBottom: 12 }}>
            <Progress
              percent={percent}
              status={rendering ? 'active' : progress.failed > 0 ? 'exception' : 'success'}
              format={() => `${progress.rendered}/${progress.total}`}
            />
            <span className={styles.subtle}>
              {rendering
                ? `Đang vẽ vé: còn ${progress.queued} vé chờ.`
                : `${progress.failed} vé vẽ lỗi — mở vé để xem cảnh báo, hoặc bấm "Vẽ lại".`}
              {rendering && progress.failed > 0 ? ` ${progress.failed} vé lỗi.` : ''}
            </span>
          </div>
        )}

        <div className={styles.toolbar}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            Thêm khách mời
          </Button>
          <Select
            allowClear
            placeholder="Trạng thái"
            style={{ width: 170 }}
            value={status}
            onChange={(v) => {
              setPage(1);
              setStatus(v);
            }}
            options={[
              ...(Object.keys(TICKET_STATUS_META) as GalaTicketStatus[]).map((s) => ({
                value: s as StatusFilter,
                label: TICKET_STATUS_META[s].text,
              })),
              { value: UNSEATED as StatusFilter, label: 'Chưa có ghế' },
            ]}
          />
          <Select
            allowClear
            placeholder="Nhóm khách"
            style={{ width: 140 }}
            value={group}
            onChange={(v) => {
              setPage(1);
              setGroup(v);
            }}
            options={groups.map((g) => ({ value: g, label: g }))}
          />
          <Input.Search
            allowClear
            placeholder="Tìm tên, @username, UID, mã, ghi chú"
            style={{ width: 280 }}
            onSearch={(v) => {
              setPage(1);
              setSearch(v);
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => afterChange()} aria-label="Tải lại danh sách" />
          <span style={{ flex: 1 }} />
          <Button icon={<DownloadOutlined />} onClick={onExport} loading={busy}>
            Xuất Excel
          </Button>
          <Button
            onClick={() =>
              guard(() => renderAll(eventId, false), 'Đã xếp các vé chưa có ảnh vào hàng đợi vẽ.', 'Không xếp vẽ được.')
            }
            loading={busy}
          >
            Vẽ vé còn thiếu
          </Button>
          <Popconfirm
            title="Vẽ lại TẤT CẢ vé?"
            description="Vé đã có ảnh cũng bị vẽ lại (ghi đè)."
            okText="Vẽ lại"
            cancelText="Huỷ"
            onConfirm={() =>
              guard(() => renderAll(eventId, true), 'Đã xếp tất cả vé vào hàng đợi vẽ lại.', 'Không xếp vẽ được.')
            }
          >
            <Button loading={busy}>Vẽ lại tất cả</Button>
          </Popconfirm>
        </div>

        {selectedIds.length > 0 && (
          <Space style={{ marginBottom: 12 }} wrap>
            <span>Đã chọn {selectedIds.length} vé</span>
            <Button size="small" onClick={rerenderSelected} loading={busy}>
              Vẽ lại
            </Button>
            <Button size="small" danger disabled={revokable.length === 0} onClick={() => setRevokeList(revokable)}>
              Thu hồi
            </Button>
            <Button size="small" type="link" onClick={() => setSelectedIds([])}>
              Bỏ chọn
            </Button>
          </Space>
        )}

        <TicketsTable
          rows={rows}
          loading={loading}
          templates={templates}
          page={page}
          limit={limit}
          total={total}
          selectedIds={selectedIds}
          onSelect={setSelectedIds}
          onPage={(p, l) => {
            setPage(p);
            setLimit(l);
          }}
          onEdit={setEditTicket}
          onLink={setLinkTarget}
          onView={setViewTicket}
          onRevoke={(t) => setRevokeList([t])}
          onRestore={(t) => guard(() => restoreTicket(eventId, t.id), 'Đã khôi phục vé.', 'Không khôi phục được vé.')}
          onDelete={(t) => guard(() => deleteTicket(eventId, t.id), 'Đã xoá vé.', 'Không xoá được vé.')}
        />
      </section>

      <TemplateEditor
        eventId={eventId}
        open={editorOpen}
        template={editingTpl}
        groups={groups}
        onClose={() => setEditorOpen(false)}
        onSaved={reloadTemplates}
      />
      <AddGuestsModal eventId={eventId} open={addOpen} onClose={() => setAddOpen(false)} onDone={afterChange} />
      <EditTicketModal
        eventId={eventId}
        ticket={editTicket}
        templates={templates}
        groups={groups}
        onClose={() => setEditTicket(null)}
        onSaved={afterChange}
      />
      <LinkTicketModal eventId={eventId} ticket={linkTarget} onClose={() => setLinkTarget(null)} onSaved={afterChange} />
      <RevokeModal
        eventId={eventId}
        tickets={revokeList}
        onClose={() => setRevokeList([])}
        onDone={async () => {
          setSelectedIds([]);
          await afterChange();
        }}
      />
      <ViewTicketModal ticket={viewTicket} onClose={() => setViewTicket(null)} />
    </div>
  );
};

export default TicketsTab;
